import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Row, Col, Input, Typography, Button, Select, Flex, Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import DataTable from '../../components/data-table';
import CommonPopconfirm from '../../components/custom-popconfirm';
import SearchIcon from '../../components/icon/SearchIcon';
import EditIcon from '../../components/icon/EditIcon';
import DeleteIcon from '../../components/icon/DeleteIcon';

import { getContracts, archiveContract, toggleContractStatus, getCategoryOptions } from './redux/slice';
import { primary, secondary, muted } from '../../utils/theme/config';

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
    { value: '', label: 'Alle Status' },
    { value: 'active', label: 'Aktiv' },
    { value: 'cancelled', label: 'Gekündigt' },
    { value: 'expired', label: 'Abgelaufen' },
    { value: 'archived', label: 'Archiviert' },
];

const INTERVAL_OPTIONS = [
    { value: '', label: 'Alle Intervalle' },
    { value: 'monthly', label: 'Monatlich' },
    { value: 'quarterly', label: 'Quartalsweise' },
    { value: 'yearly', label: 'Jährlich' },
    { value: 'once', label: 'Einmalig' },
];

const SORT_OPTIONS = [
    { value: 'name', label: 'Name' },
    { value: 'costAmount', label: 'Kosten' },
    { value: 'startDate', label: 'Startdatum' },
    { value: 'endDate', label: 'Enddatum' },
];

const statusColors = {
    active: primary,
    cancelled: secondary,
    expired: '#fa8c16',
    archived: muted,
};

const statusLabels = {
    active: 'Aktiv',
    cancelled: 'Gekündigt',
    expired: 'Abgelaufen',
    archived: 'Archiviert',
};

const intervalLabels = {
    monthly: 'monatlich',
    quarterly: 'quartalsweise',
    yearly: 'jährlich',
    once: 'einmalig',
};

const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('de-DE');
};

const ContractList = ({ permission = {} }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const auth = useSelector(state => state.auth);
    const contracts = useSelector(state => state.contracts);
    const { list, totalRecords, loading, categoryOptions } = contracts;

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortBy, setSortBy] = useState('name');
    const [order, setOrder] = useState('asc');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterInterval, setFilterInterval] = useState('');
    const debounceRef = useRef(null);

    const organizer = auth?.decodeToken?.organizer;

    useEffect(() => {
        if (organizer) {
            dispatch(getCategoryOptions(organizer));
        }
    }, [organizer]);

    useEffect(() => {
        if (organizer) {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                fetchList();
            }, 400);
        }
        return () => clearTimeout(debounceRef.current);
    }, [organizer, search, page, pageSize, sortBy, order, filterCategory, filterStatus, filterInterval]);

    const fetchList = () => {
        dispatch(getContracts({
            search: search.trim(),
            sortBy,
            order,
            page,
            limit: pageSize,
            organizer,
            filter: {
                category: filterCategory || undefined,
                status: filterStatus || undefined,
                costInterval: filterInterval || undefined,
            },
        }));
    };

    const handleSortChange = (_, __, sorter) => {
        if (sorter?.field) {
            setSortBy(sorter.field);
            setOrder(sorter.order === 'descend' ? 'desc' : 'asc');
        }
    };

    const handleResetFilters = () => {
        setFilterCategory('');
        setFilterStatus('');
        setFilterInterval('');
        setSearch('');
        setPage(1);
    };

    const hasActiveFilters = filterCategory || filterStatus || filterInterval || search;

    const getFilterData = () => ({
        search: search.trim(),
        sortBy,
        order,
        page,
        limit: pageSize,
        organizer,
        filter: {
            category: filterCategory || undefined,
            status: filterStatus || undefined,
            costInterval: filterInterval || undefined,
        },
    });

    const columns = [
        {
            title: 'Nr.',
            dataIndex: 'uniqueIdentifier',
            key: 'uniqueIdentifier',
            width: 100,
        },
        {
            title: 'Vertragsname',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            ellipsis: true,
        },
        {
            title: 'Partner',
            dataIndex: 'partner',
            key: 'partner',
            ellipsis: true,
        },
        {
            title: 'Kategorie',
            dataIndex: ['categoryDetails', 'name'],
            key: 'category',
            render: (_, record) => record?.categoryDetails?.name || '—',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={statusColors[status] || muted}>
                    {statusLabels[status] || status}
                </Tag>
            ),
        },
        {
            title: 'Kosten',
            dataIndex: 'costAmount',
            key: 'costAmount',
            sorter: true,
            render: (val, record) => val ? `${formatCurrency(val)} / ${intervalLabels[record.costInterval] || record.costInterval}` : '—',
        },
        {
            title: 'Enddatum',
            dataIndex: 'endDate',
            key: 'endDate',
            sorter: true,
            render: (val) => formatDate(val),
        },
        {
            title: 'Kündigungsfrist',
            dataIndex: 'noticePeriod',
            key: 'noticePeriod',
            render: (val) => val ? `${val} Tage` : '—',
        },
    ];

    if (Object.keys(permission).length > 0 && (permission?.canUpdate || permission?.canDelete)) {
        columns.push({
            title: <Flex justify='center'><Text>Aktion</Text></Flex>,
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Flex justify='center' gap={4}>
                    {permission?.canUpdate && (
                        <Button
                            type='text'
                            className='padding-1'
                            onClick={() => navigate(`/contract-manager/edit/${record._id}`)}
                        >
                            <EditIcon role='button' />
                        </Button>
                    )}
                    {permission?.canDelete && (
                        <Fragment>
                            <CommonPopconfirm
                                icon={null}
                                title='Vertrag archivieren'
                                description='Möchten Sie diesen Vertrag wirklich archivieren?'
                                onConfirm={() => dispatch(archiveContract({ id: record._id, filterData: getFilterData() }))}
                                cancelButtonProps={{ danger: true, size: 'small', type: 'primary' }}
                            >
                                <Button type='text' className='padding-1'>
                                    <DeleteIcon role='button' />
                                </Button>
                            </CommonPopconfirm>
                        </Fragment>
                    )}
                </Flex>
            ),
        });
    }

    return (
        <Fragment>
            <Row justify='space-between' align='middle' className='bg-white padding-1 rounded-1' gutter={[16, 16]}>
                <Col xs={24} sm={16} md={16} lg={16} xl={16}>
                    <Row align='middle' gutter={[16, 16]}>
                        <Col flex='none'>
                            <Title level={4} type='success'>Verträge</Title>
                        </Col>
                        <Col flex='auto'>
                            <Input
                                value={search}
                                placeholder='Suche...'
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                prefix={<SearchIcon color='#000' size={20} />}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                    <Flex justify='end'>
                        {Object.keys(permission).length > 0 && permission?.canCreate && (
                            <Button onClick={() => navigate('/contract-manager/add')}>
                                Neuer Vertrag
                            </Button>
                        )}
                    </Flex>
                </Col>
            </Row>

            <Row gutter={[12, 12]} className='padding-top-1 bg-white padding-1 rounded-1' style={{ marginTop: 12 }}>
                <Col xs={24} sm={8} md={6}>
                    <Select
                        allowClear
                        placeholder='Alle Kategorien'
                        style={{ width: '100%' }}
                        value={filterCategory || undefined}
                        options={categoryOptions || []}
                        onChange={(val) => { setFilterCategory(val || ''); setPage(1); }}
                    />
                </Col>
                <Col xs={24} sm={8} md={6}>
                    <Select
                        allowClear
                        placeholder='Alle Status'
                        style={{ width: '100%' }}
                        value={filterStatus || undefined}
                        options={STATUS_OPTIONS.filter(o => o.value)}
                        onChange={(val) => { setFilterStatus(val || ''); setPage(1); }}
                    />
                </Col>
                <Col xs={24} sm={8} md={6}>
                    <Select
                        allowClear
                        placeholder='Alle Intervalle'
                        style={{ width: '100%' }}
                        value={filterInterval || undefined}
                        options={INTERVAL_OPTIONS.filter(o => o.value)}
                        onChange={(val) => { setFilterInterval(val || ''); setPage(1); }}
                    />
                </Col>
                <Col xs={24} sm={8} md={6}>
                    <Flex gap={8}>
                        <Select
                            placeholder='Sortierung'
                            style={{ flex: 1 }}
                            defaultValue='name'
                            options={SORT_OPTIONS}
                            onChange={(val) => setSortBy(val)}
                        />
                        {hasActiveFilters && (
                            <Button onClick={handleResetFilters}>
                                Zurücksetzen
                            </Button>
                        )}
                    </Flex>
                </Col>
            </Row>

            <Row className='padding-top-1'>
                <Col xs={24}>
                    <DataTable
                        columns={columns}
                        data={list}
                        pageSize={pageSize}
                        currentPage={page}
                        total={totalRecords}
                        scrollX={1100}
                        showSorterTooltip={false}
                        isPagination={true}
                        loading={loading}
                        handleOnChange={(newPage, newSize) => {
                            setPage(newPage);
                            setPageSize(newSize);
                        }}
                        onChange={handleSortChange}
                        rowKey={(record) => record._id}
                        onRow={(record) => ({
                            onClick: () => navigate(`/contract-manager/detail/${record._id}`),
                            style: { cursor: 'pointer' },
                        })}
                    />
                </Col>
            </Row>
        </Fragment>
    );
};

export default ContractList;
